import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FaEye, FaEyeSlash, FaLock, FaShieldAlt, FaArrowRight } from 'react-icons/fa'
import { useAdmin } from '../../context/AdminContext'

const LOCK_DURATION_MS  = 5 * 60 * 1000   // 5 min
const MAX_ATTEMPTS      = 5

export default function AdminLogin({ onSuccess }) {
  const { login } = useAdmin()
  const [pw,       setPw]       = useState('')
  const [showPw,   setShowPw]   = useState(false)
  const [error,    setError]    = useState('')
  const [loading,  setLoading]  = useState(false)
  const [attempts, setAttempts] = useState(0)
  const [locked,   setLocked]   = useState(false)
  const [lockLeft, setLockLeft] = useState(0)

  // Countdown when locked
  useEffect(() => {
    if (!locked) return
    const iv = setInterval(() => {
      setLockLeft(p => {
        if (p <= 1000) { clearInterval(iv); setLocked(false); setAttempts(0); setError(''); return 0 }
        return p - 1000
      })
    }, 1000)
    return () => clearInterval(iv)
  }, [locked])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (locked || loading) return
    if (!pw.trim()) { setError('Please enter the admin password.'); return }

    setLoading(true)
    setError('')

    // Slight artificial delay so it doesn't feel instant
    setTimeout(() => {
      const ok = login(pw)
      if (ok) {
        onSuccess()
      } else {
        const next = attempts + 1
        setAttempts(next)
        setPw('')
        if (next >= MAX_ATTEMPTS) {
          setLocked(true)
          setLockLeft(LOCK_DURATION_MS)
          setError(`Too many failed attempts. Locked for 5 minutes.`)
        } else {
          setError(`Incorrect password — ${MAX_ATTEMPTS - next} attempt${MAX_ATTEMPTS - next !== 1 ? 's' : ''} remaining.`)
        }
      }
      setLoading(false)
    }, 700)
  }

  const mins = Math.ceil(lockLeft / 60000)

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden"
      style={{ background: '#050507' }}>

      {/* Ambient background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full blur-[140px] opacity-[0.07]"
          style={{ background: 'radial-gradient(circle, #3B82F6 0%, transparent 70%)' }} />
        <div className="absolute bottom-10 right-20 w-64 h-64 rounded-full blur-[100px] opacity-[0.05]"
          style={{ background: 'radial-gradient(circle, #8B5CF6 0%, transparent 70%)' }} />
        {/* Grid */}
        <div className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: `linear-gradient(rgba(59,130,246,0.5) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(59,130,246,0.5) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 28, scale: 0.96 }}
        animate={{ opacity: 1, y: 0,  scale: 1 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-[400px] relative z-10"
      >
        {/* ── Logo / Brand ── */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 220, damping: 14, delay: 0.1 }}
            className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center shadow-lg"
            style={{
              background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
              boxShadow: '0 0 40px rgba(59,130,246,0.3)',
            }}
          >
            <FaShieldAlt size={22} className="text-white" />
          </motion.div>
          <h1 className="font-display font-bold text-2xl text-white tracking-tight">Admin Panel</h1>
          <p className="font-mono text-xs text-slate-600 mt-1.5 tracking-widest">SSP PORTFOLIO · SECURE ACCESS</p>
        </div>

        {/* ── Card ── */}
        <motion.div
          className="rounded-3xl border border-white/[0.07] p-8"
          style={{
            background: 'rgba(10,10,16,0.85)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 0 0 1px rgba(255,255,255,0.04), 0 32px 80px rgba(0,0,0,0.5)',
          }}
        >
          <form onSubmit={handleSubmit} noValidate>
            {/* Field */}
            <div className="mb-6">
              <label htmlFor="admin-pw" className="block font-mono text-xs text-slate-500 tracking-widest uppercase mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                  <FaLock size={12} className="text-slate-600" />
                </div>
                <input
                  id="admin-pw"
                  type={showPw ? 'text' : 'password'}
                  value={pw}
                  onChange={e => { setPw(e.target.value); setError('') }}
                  placeholder="Enter admin password"
                  disabled={locked || loading}
                  autoComplete="current-password"
                  maxLength={100}
                  className={`w-full pl-10 pr-12 py-3.5 rounded-xl text-sm text-slate-200 placeholder-slate-700 outline-none transition-all duration-200
                    bg-white/[0.04] border disabled:opacity-40 disabled:cursor-not-allowed
                    ${error
                      ? 'border-red-500/40 focus:border-red-500/60 focus:ring-2 focus:ring-red-500/15'
                      : 'border-white/10 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/15'
                    }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(v => !v)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-400 transition-colors"
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPw ? <FaEyeSlash size={13} /> : <FaEye size={13} />}
                </button>
              </div>

              {/* Error / lock msg */}
              <AnimatePresence>
                {error && (
                  <motion.p
                    key={error}
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className={`mt-2 text-xs font-mono ${locked ? 'text-orange-400' : 'text-red-400'}`}
                  >
                    {locked ? `🔒 ${error} (${mins} min left)` : error}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>

            {/* Submit */}
            <motion.button
              type="submit"
              disabled={locked || loading || !pw.trim()}
              className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-xl font-display font-semibold text-sm text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
              whileHover={!locked && !loading && pw.trim() ? { scale: 1.02, boxShadow: '0 0 28px rgba(59,130,246,0.35)' } : {}}
              whileTap={!locked && !loading && pw.trim() ? { scale: 0.97 } : {}}
            >
              {loading ? (
                <>
                  <motion.span
                    className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
                  />
                  Verifying…
                </>
              ) : (
                <>
                  <FaLock size={11} />
                  Access Dashboard
                  <FaArrowRight size={11} />
                </>
              )}
            </motion.button>
          </form>

          {/* Hint */}
          <div className="mt-6 pt-5 border-t border-white/[0.05] text-center">
            <p className="font-mono text-xs text-slate-800">
              Example: <span className="text-slate-600 select-all">Shankar@12</span>
            </p>
          </div>
        </motion.div>

        {/* Back link */}
        <p className="text-center mt-5">
          <a href="/" className="font-mono text-xs text-slate-700 hover:text-slate-400 transition-colors">
            ← Back to portfolio
          </a>
        </p>
      </motion.div>
    </div>
  )
}
